import { createFileRoute } from "@tanstack/react-router";
import getPageTitle from "lib/utils/getPageTitle";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../components/withAuthentication";

function DesignMaterialDocsPage() {
  return (
    <div className="docs">
      <Head>
        <title>{getPageTitle("Design Material Documentation")}</title>
        <meta name="robots" content="noindex" />

        <style>{`
            .docs {
                margin: 0;
                padding: 20px;
            }

            .code {
              font-family: monospace;
            }

            .oldParam {
              color: firebrick;
            }

            .param2 {
              color: 	seagreen;
            }

            .param3 {
              color: darkorange;
            }

            .materialTemplateName {
              color: firebrick;
            }

            .layerConfigLibraryName {
              color: seagreen;
            }

            .layerConfigName {
              color: darkgreen;
            }

            .colorLibraryName {
              color: darkorange;
            }

            .colorName {
              color: red;
            }

            th, td {
              border: 1px solid black;
              padding: 10px !important;
            }
          `}</style>
      </Head>

      <h1>Design material documentation</h1>

      <p>The design material system extends three.js physically based materials with new capabilities.</p>

      <h2>Detail geometry / SDF</h2>

      <p>
        Surface detail can be introduced by tweaking the SDF parameters. Each pattern is a lattice of evenly spaced
        boxes (rectangular prisms). The design material shader traces rays into this simulated geometry and modifies the
        lighting computations (surface normal etc.) accordingly. The detail geometry does not affect the silhouette of
        the geometry. Bevel values are relative to the edges of each box.
      </p>

      <p>The SDF patterns affect two masks that can be referenced in the layer system:</p>

      <ul>
        <li>
          <em>SDF depth mask</em> is based on the depth from the SDF surface.
        </li>
        <li>
          <em>SDF random</em> generates a pseudorandom value ranging from 0 to 1 for each SDF box. This mask can be
          overridden with a texture. Note that the mask texture should not be anti-aliased.
        </li>
      </ul>

      <h2>Parallax maps</h2>

      <p>
        Using an uploaded parallax map is an alternative to using the SDF geometry. If the material height map is set,
        all SDF parameters are ignored and the height map is used by the parallax effect.
      </p>

      <h2>Texture scaling</h2>

      <p>
        The distance unit used by design material controls is inches, so setting layer or texture scale to 120 will make
        the texture 120 inches (or 10 feet) wide.
      </p>

      <p>
        If the scale is set to 120, then each pixel of a 1024x1024 texture will be 120/1024 = 0.1172 inches wide. The
        material editor reports the current scale of the main mask texture if it&apos;s in use.
      </p>

      <h2>Layers</h2>

      <p>
        Layers are alpha-masked patterns overlaid on top of each other to alter the material albedo, roughness,
        metalness, and bump map. They are suitable for adding fine detail such as wood grain, an uneven rock or painted
        surface, as well as introducing large-scale color variation.
      </p>

      <p>
        Each layer can have a mask associated with it. The mask can be a combination of the two SDF masks, mixed
        according to the sdfMaskRandomness parameter, or a texture-based mask.
      </p>

      <p>
        UV coordinates used for texture-based layer data can be transformed using an affine transform. If uvTransformID{" "}
        <b>layer</b>/<b>mask</b> is chosen, then the transform on the layer or mask is used. If
        <b>normalMap</b> is chosen, then the layer references the normal map transform.
      </p>

      <h3>Layer color gradient</h3>

      <p>
        Layers can have a color gradient with 2-4 color stops. The section before the first stop is filled with solid
        color using the first color, and the section after the last stop is filled with solid color using the last
        color.
      </p>

      <ul>
        <li>
          If colorCount is 2, then just color0 and color1 get used. color0Pos and color1Pos determine the positions of
          the colors on the gradient.
        </li>
        <li>
          If colorCount is 3, then color0, color1 and color2 get used. color0Pos, color1Pos and color2Pos determine the
          positions of the colors on the gradient.
        </li>
        <li>
          If colorCount is 4, then color0, color1, color2 and color3 get used. color0Pos, color1Pos, color2Pos, and
          color3Pos determine the positions of the colors on the gradient.
        </li>
      </ul>

      <h3>UV offset</h3>

      <p>
        To introduce random variability, random UV offset can be applied to the UV coordinates used to sample the
        layers. The offset has a random angle and its length is relative to the texture it&apos;s applied to. The UV
        offset is always based on the <em>SDF random</em> mask, but its effect on each layer and its mask can be
        controlled with strength parameters.
      </p>

      <h2>Normal map handedness</h2>

      <p>Normal maps should be authored using the right handed (OpenGL) convention.</p>

      <p>Correctly oriented normal map (right-handed):</p>
      <img src="data:image/png;base64,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" />

      <p>Incorrectly oriented normal map (left-handed):</p>
      <img src="data:image/png;base64,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" />

      <h2>Hatch material</h2>

      <p>
        Design materials can be associated with a hatch material, shown in a small preview canvas in the corner of the
        editor. Normally the automatically generated hatch material can be used.
      </p>

      <h2>Design material editor</h2>

      <p>
        In addition to editing the design material parameters, the editor exposes several debug visualization modes as
        well as giving control over lighting. The time-of-day lighting modes in the editor use the same lighting
        settings as Showroom.
      </p>

      <h2>Design material files</h2>

      <p>Design materials are defined using 4 different types of files: </p>

      <table>
        <tbody>
          <tr>
            <th>File type</th>
            <th>Content</th>
            <th>Extension</th>
          </tr>
          <tr>
            <td>Material template</td>
            <td>Complete material, same as an old material file.</td>
            <td>.default.json</td>
          </tr>
          <tr>
            <td>Layer configuration</td>
            <td>Just the layers, masks and base color that override material template values</td>
            <td>.layers.json</td>
          </tr>
          <tr>
            <td>Layer configuration library</td>
            <td>List of layer configuration names</td>
            <td>.llib.json</td>
          </tr>
          <tr>
            <td>Color library</td>
            <td>List of named RGB color values</td>
            <td>.clib.json</td>
          </tr>
        </tbody>
      </table>

      <p>
        The templates specify which layer configuration libraries and/or color libraries are compatible with them, and
        they have a flag telling if the template can be used as such without specifying a color or layer configuration.
      </p>

      <h2>Design material ids and palettes</h2>

      <p>
        Design materials can be referenced by <b>palettes</b> and some individual building entities like stairs and
        railings.
      </p>

      <p>
        Design materials are identified by using <span className="code">MaterialSrc</span> objects. They contain the id
        of the template, (optionally) the id of the layer config, and (optionally) the id of the color. Materials that
        use a layer config can also have their base color overridden, in which case the color is called an override
        color. The palette editor in Studio can be used to look up valid materials.
      </p>

      <p>
        In palettes that are defined in code, you can look up valid materials from the material path library. Examples:
      </p>
      <table>
        <tr>
          <th>Material template is used as such:</th>
          <td>
            <span className="code">
              <span className="materialTemplateName">&lt;MaterialTemplateName&gt;</span>.Default
            </span>
          </td>
        </tr>
        <tr>
          <th>Material with a layer config:</th>
          <td>
            <span className="code">
              <span className="materialTemplateName">&lt;MaterialTemplateName&gt;</span>.L.
              <span className="layerConfigLibraryName">&lt;LayerConfigLibraryName&gt;</span>.
              <span className="layerConfigName">&lt;LayerConfigName&gt;</span>
            </span>
          </td>
        </tr>
        <tr>
          <th>Material with color:</th>
          <td>
            <span className="code">
              {"{ "}
              ...<span className="materialTemplateName">&lt;MaterialTemplateName&gt;</span>.C, colorId: DMC.
              <span className="colorLibraryName">&lt;ColorLibraryName&gt;</span>.
              <span className="colorName">&lt;ColorName&gt;</span>
              {" }"}
            </span>
          </td>
        </tr>
        <tr>
          <th>Material with a layer config and override color:</th>
          <td>
            <span className="code">
              {"{ "}
              ...<span className="materialTemplateName">&lt;MaterialTemplateName&gt;</span>.L.
              <span className="layerConfigLibraryName">&lt;LayerConfigLibraryName&gt;</span>.
              <span className="layerConfigName">&lt;LayerConfigName&gt;</span>, colorId: DMC.
              <span className="colorLibraryName">&lt;ColorLibraryName&gt;</span>.
              <span className="colorName">&lt;ColorName&gt;</span>
              {" }"}
            </span>
          </td>
        </tr>
      </table>

      <p>
        Some parts of the code also still support the legacy DesignMaterialIds, accessible through the autogenerated
        materialLibrary.ts. The mapping from ids to MaterialSrc is defined in materialIdMapping.json.
      </p>

      <p>
        When a MaterialSrc object needs to be written into a database or in a file, it gets written out as numerical
        ids. The numerical ids stay valid even if materials are renamed. See the{" "}
        <a href="./material-id">the material ID debug page</a> for technical details of material ids.
      </p>

      <h2>Masonry files</h2>

      <p>
        Masonry files group different types of brick patterns together. The &quot;base&quot; pattern or &quot;trim
        kind&quot; typically links to a Design material variant with bricks in a staggered layout. &quot;Rowlock&quot;
        and &quot;header&quot; layouts have bricks tightly packed together in the same orientation.
      </p>

      <h2>Renaming and merging material data</h2>

      <p>Design material files can be renamed, merged, or split using the higharc CLI. Usage:</p>
      <ul className="code">
        <li>
          yarn higharc rename material-template <span className="oldParam">&lt;old template name&gt;</span>{" "}
          <span className="param2">&lt;new template name&gt;</span>
        </li>
        <li>
          yarn higharc rename masonry <span className="oldParam">&lt;old masonry name&gt;</span>{" "}
          <span className="param2">&lt;new masonry name&gt;</span>
        </li>
        <li>
          yarn higharc rename layer-config-lib <span className="oldParam">&lt;old layer config lib name&gt;</span>{" "}
          <span className="param2">&lt;new layer config lib name&gt;</span>
        </li>
        <li>
          yarn higharc rename layer-config <span className="oldParam">&lt;old layer config name&gt;</span>{" "}
          <span className="param2">&lt;new layer config name&gt;</span>
        </li>
        <li>
          yarn higharc rename color-lib <span className="oldParam">&lt;old color lib name&gt;</span>{" "}
          <span className="param2">&lt;new color lib name&gt;</span>
        </li>
        <li>
          yarn higharc rename color <span className="param3">&lt;color lib name&gt;</span>.
          <span className="oldParam">&lt;old color name&gt;</span>{" "}
          <span className="param2">&lt;new color name&gt;</span>
        </li>
        <li>
          yarn higharc rename split-layer-config-lib{" "}
          <span className="oldParam">&lt;split layer config lib name&gt;</span>{" "}
          <span className="param2">&lt;new layer config lib name&gt;</span>
        </li>
        <li>
          yarn higharc rename split-color-lib <span className="oldParam">&lt;split color lib name&gt;</span>{" "}
          <span className="param2">&lt;new color lib name&gt;</span>
        </li>
      </ul>

      <p>All the name parameters of the script except for color names are file names without file extension.</p>

      <p>
        If a file or config with the new name already exists, the script asks for confirmation and then merges them.
        This can be used to combine two color libraries or layer config libraries, for example. Merging is also a safe
        way to delete design material files - in case there are some references to the deleted file, they will be
        pointing to the merge target afterwards. The values from the merge target take precedence over the values from
        the object being renamed.
      </p>

      <p>
        After running the rename script, <span className="code">yarn higharc gen material-library</span> needs to be run
        to update autogenerated files. The rename script should also update most material references in code
        automatically.
      </p>

      <h2>Mask textures</h2>

      <p>
        Mask textures are stored in git in <span className="code">apps/web/public/static/textures-maskonly</span>. New
        textures can be added by simply adding a new PNG file to that directory and running a script to update the mask
        textures index:
      </p>

      <p>
        <span className="code">yarn higharc materials compress-textures</span>
      </p>

      <p>
        The compressTextures script performs some checks on the textures to see that they&apos;re in the correct format.
        The PNG files should conform to the following spec:
      </p>

      <ul>
        <li>Greyscale PNG</li>
        <li>Size 1024x1024 pixels</li>
        <li>NOT antialiased - different shades are used only to tell apart different areas of the image</li>
        <li>Areas outside of the mask colored black</li>
        <li>Areas inside of the mask colored white or light grey (at least 128/255 brightness).</li>
        <li>
          Each continuous area that&apos;s completely surrounded by black is treated as separate, but shades of grey can
          be used to separate different areas.
        </li>
      </ul>

      <h2>Material library scripts</h2>

      <p>We have multiple scripts for maintaining material library data.</p>

      <ul>
        <li>
          <span className="code">yarn higharc gen material-library</span> Update the material library so materials and
          masonry definitions can be accessed by the code implementing Higharc studio etc.
        </li>
        <li>
          <span className="code">yarn higharc materials group-to-masonry</span> Group existing materials to masonry
          files automatically based on their naming. This can be used to define new masonry files, but it also
          automatically updates pre-existing ones. Run <span className="code">yarn higharc gen material-library</span>{" "}
          both before and after this script!
        </li>
        <li>
          <span className="code">yarn higharc gen palette-library</span> Generates palette definition files from code.
          We define palettes in code so we can edit them conveniently with autocomplete support, but convert them to
          JSON files so that we can load them on demand, rather than increasing our build size.
        </li>
      </ul>

      <h2>Performance</h2>

      <h3>Material baking</h3>

      <p>
        On lower-end platforms, we use a simplified version of the design material shader that is based on baking the
        material into a repeating color texture. This can be tested by using the bakeColorTexture option in Material
        Load Options.
      </p>

      <p>
        The appearance of the baked material will be slightly different from the original material, since local shadows
        are not supported and local AO is baked into the color texture. The dimensions of the layers are also adjusted
        to fit the size of the color texture.
      </p>

      <h3>Loading performance</h3>

      <p>
        The <a href="./material-appearance-test">material appearance test</a> can be used to load a larger set of
        materials at once to evaluate loading performance. The test introduces random variations to the shader code to
        circumvent any caching mechanisms the browser and GPU drivers may employ, so the performance should always be
        similar to a cold load.
      </p>

      <p>
        The analyze function in the design material editor can be used to check how many different shader variants
        design materials use.
      </p>
    </div>
  );
}

export const Route = createFileRoute("/debug/design-material-docs")({
  component: withAuthentication(DesignMaterialDocsPage, { superAdmin: true }),
});
